import React, { useEffect, useMemo, useState } from "react";
import StatusCard from "../../Components/Statuscard/StatusCard";
import ProgressChartCard from "../../Components/ProgressChartCard/ProgressChartCard";
import CommonTable2, {
  type Column,
} from "../../Components/CommonTable/CommonTable2";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Button } from "@mui/material";
import {
  Typography,
  Box,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem as MuiMenuItem,
  Chip,
  InputAdornment,
  TextField,
  Divider,
  Paper,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../Redux/Store/Hooks";
import { TutorDashboardClassesRead } from "../../../../Redux/Slices/Booklet/Tutor/Dashboard/DashboardAssignedClasses";
import { TutorDashboardStatusRead } from "../../../../Redux/Slices/Booklet/Tutor/Dashboard/DashboardStatus";
import { TutorDashboardSummaryRead } from "../../../../Redux/Slices/Booklet/Tutor/Dashboard/DashboardSummary";
import { TutorDashboardCalendar } from "../../../../Redux/Slices/Booklet/Tutor/Dashboard/DashbaoardCalender";
import CalendarView from "../../Components/Calendar/CalenderView";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import AlertBox from "../../Components/AlertBox/AlertBox";
import { useLocalTZ } from "../../../../Hooks/useLocalTZ";

interface ClassSection {
  day: string;
  center: string;
  center_id?: string;
  YearLevel: string;
  YearLevel_id?: string;
  csms_id: string;
  subject: string;
  subject_id?: string;
  date: string;
  originalDate?: string | number | Date | null;
  print_request_status: string;
  request_ids?: string[];
  [key: string]: unknown;
}

const TutorDashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { formatDateShort, isTomorrow } = useLocalTZ();

  const { data: ClassesData, isLoading: ClassesLoading } = useAppSelector(
    (state) => state.TutorDashboardClassesRead
  );

  const { data: SummaryData } = useAppSelector(
    (state) => state.TutorDashboardSummaryRead
  );

  const { data: StatusData } = useAppSelector(
    (state) => state.TutorDashboardStatusRead
  );

  const { data: CalenderData, isLoading: dateLoading } = useAppSelector(
    (state) => state.TutorDashboardCalendar
  );

  const [filters, setFilters] = React.useState<any>({});
  const [tempFilters, setTempFilters] = React.useState<any>({});
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const dropdownOpen = Boolean(anchorEl);
  const [openPopup, setOpenPopup] = React.useState(false);
  const [selectedClasses, setSelectedClasses] = React.useState<any[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    dispatch(TutorDashboardClassesRead());
    dispatch(TutorDashboardStatusRead());
    dispatch(TutorDashboardSummaryRead());
    dispatch(TutorDashboardCalendar());
  }, []);

  const upcomingClasses: ClassSection[] = useMemo(() => {
    if (!ClassesData?.data) return [];

    return ClassesData.data.map((item: any) => ({
      date: formatDateShort(item.date),
      originalDate: item.date,

      day: item.day || "-",
      csms_id: item.csms_id || "-",
      YearLevel: item.year_group?.year_group || "-",
      YearLevel_id: item.year_group?.id || "-",
      subject_id: item.subject?.id || "-",
      center_id: item.center?.center_id || "-",
      center: item.center?.center_name || "-",
      print_request_status: item.print_request_status || "-",
      subject: item.subject?.name || "-",
      request_ids: item.request_ids ?? [],
    }));
  }, [ClassesData]);

  useEffect(() => {
    const alertClosed = sessionStorage.getItem("printerAlertClosed");
    if (alertClosed === "true") {
      setOpen(false);
      return;
    }

    if (!upcomingClasses || upcomingClasses.length === 0) {
      setOpen(false);
      return;
    }

    const now = new Date();

    const tomorrow = new Date(
      Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1)
    );
    const tomorrowYear = tomorrow.getUTCFullYear();
    const tomorrowMonth = tomorrow.getUTCMonth();
    const tomorrowDayOfMonth = tomorrow.getUTCDate();

    const hasPendingPrint = upcomingClasses.some((cls) => {
      if (!cls.originalDate) return false;
      if (cls.print_request_status !== "not_yet_requested") return false;

      return isTomorrow(cls.originalDate);
    });

    setOpen(hasPendingPrint);
  }, [upcomingClasses]);

  const centerOptions = useMemo(() => {
    const centers = new Set();
    upcomingClasses.forEach((c) => centers.add(c.center));
    return Array.from(centers);
  }, [upcomingClasses]);

  const statusOptions = [
    { label: "Printing Completed", value: "printed" },
    { label: "Requested", value: "requested" },
    { label: "In Progress", value: "approved" },
    { label: "Rejected / Failed", value: "rejected" },
    { label: "Not Started", value: "not_yet_requested" },
  ];

  // Enhanced column filters with all fields as dropdowns where possible for better UX
  const columnFilters: any = {
    date: {
      type: "select",
      label: "Date",
      options: () => {
        const dates = new Set(
          upcomingClasses.map((c) => c.date).filter(Boolean)
        );
        return ["All", ...Array.from(dates)].map((opt) => ({
          value: opt === "All" ? "" : opt,
          label: opt,
        }));
      },
    },
    center: {
      type: "select",
      label: "Center",
      options: ["All", ...centerOptions].map((opt) => ({
        value: opt === "All" ? "" : opt,
        label: opt,
      })),
    },
    day: {
      type: "select",
      label: "Day",
      options: [
        "All",
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ].map((opt) => ({ value: opt === "All" ? "" : opt, label: opt })),
    },
    YearLevel: {
      type: "select",
      label: "Year Level",
      options: () => {
        const levels = new Set(
          upcomingClasses.map((c) => c.YearLevel).filter(Boolean)
        );
        return ["All", ...Array.from(levels)].map((opt) => ({
          value: opt === "All" ? "" : opt,
          label: opt,
        }));
      },
    },
    subject: {
      type: "select",
      label: "Subject",
      options: () => {
        const subjects = new Set(
          upcomingClasses.map((c) => c.subject).filter(Boolean)
        );
        return ["All", ...Array.from(subjects)].map((opt) => ({
          value: opt === "All" ? "" : opt,
          label: opt,
        }));
      },
    },
    print_request_status: {
      type: "select",
      label: "Status",
      options: [{ value: "", label: "All" }, ...statusOptions],
    },
  };

  const getOptions = useMemo(
    () => (key: string) => {
      const config = columnFilters[key];
      if (!config) return [];
      return typeof config.options === "function"
        ? config.options()
        : config.options;
    },
    [upcomingClasses]
  );

  const filteredUpcomingClasses = useMemo(() => {
    return upcomingClasses.filter((row) => {
      return Object.keys(filters).every((key) => {
        const filterValue = filters[key];
        if (
          !filterValue ||
          (Array.isArray(filterValue) && filterValue.length === 0)
        )
          return true;

        const rowValue = String(row[key] || "");
        const filterValues = Array.isArray(filterValue)
          ? filterValue
          : [filterValue];

        return filterValues.some((fv: string) => rowValue === fv);
      });
    });
  }, [upcomingClasses, filters]);

  const calendarEvents: any = useMemo(() => {
    if (!CalenderData?.data) return [];
    const { requests, mapped_classes } = CalenderData.data;

    // Map csm_id to printing_status from requests
    const statusMap = new Map<string, string>();
    requests.forEach((req: any) => {
      const status = req.printing_status;
      req.carts.forEach((cart: any) => {
        const csmId = cart.assigned_class_selection;
        if (csmId) {
          statusMap.set(csmId, status);
        }
      });
    });

    return mapped_classes.map((cls: any) => ({
      date: (cls?.date || "").split("T")[0],
      type: "class" as const,
      data: {
        ...cls,
        printing_status: statusMap.get(cls.csm_id) || "not_yet_started",
      },
    }));
  }, [CalenderData]);

  const { formatDate } = useLocalTZ();

  const printingData = useMemo(() => {
    const printing = StatusData?.data?.printing_status;
    if (!printing) return [];

    return [
      { name: "Completed", value: printing.completed || 0 },
      { name: "Not Yet Started", value: printing.not_yet_started || 0 },
      { name: "In Progress", value: printing.in_progress || 0 },
      { name: "Failed", value: printing.failed || 0 },
    ];
  }, [StatusData]);

  const printingTotal = useMemo(() => {
    const printing = StatusData?.data?.printing_status;
    if (!printing) return 0;
    return (
      (printing.completed || 0) +
      (printing.not_yet_started || 0) +
      (printing.in_progress || 0) +
      (printing.failed || 0)
    );
  }, [StatusData]);

  const requestData = useMemo(() => {
    const request = StatusData?.data?.request_status;
    if (!request) return [];

    return [
      { name: "Approved", value: request.approved || 0 },
      { name: "Pending", value: request.pending || 0 },
      { name: "Rejected", value: request.rejected || 0 },
    ];
  }, [StatusData]);

  const requestTotal = useMemo(() => {
    const request = StatusData?.data?.request_status;
    if (!request) return 0;
    return (
      (request.approved || 0) + (request.pending || 0) + (request.rejected || 0)
    );
  }, [StatusData]);

  // Class Statistics Data
  const classData = useMemo(() => {
    const classStats = StatusData?.data?.class_statistics;
    if (!classStats) return [];

    return [
      { name: "Completed", value: classStats.completed || 0 },
      { name: "Pending", value: classStats.pending || 0 },
    ];
  }, [StatusData]);

  const classTotal = useMemo(() => {
    const classStats = StatusData?.data?.class_statistics;
    if (!classStats) return 0;
    return classStats.assigned || 0;
  }, [StatusData]);

  // Status Card Data
  const totalClassesAssigned =
    SummaryData?.data?.total_classes_assigned_this_month || 0;

  const lastClassTaken = SummaryData?.data?.last_class_taken_overall;
  const lastClassSubject = lastClassTaken?.subject || "-";
  const lastClassYearGroup = lastClassTaken?.year_group?.name;
  const lastClassDate = lastClassTaken?.date
    ? formatDate(lastClassTaken.date)
    : "";

  const lastBookletRequest = SummaryData?.data?.last_booklet_request_overall;
  const lastBookletSubject = lastBookletRequest?.subject || "-";
  const lastBookletYearGroup = lastBookletRequest?.year_group?.name || "-";
  const lastBookletDate = lastBookletRequest?.date
    ? formatDate(lastBookletRequest.date)
    : "-";

  const columns: Column[] = [
    { field: "date", headerName: "Date", sortable: true },
    { field: "center", headerName: "Center", sortable: true },
    { field: "day", headerName: "Day" },
    { field: "YearLevel", headerName: "Year Level" },
    { field: "subject", headerName: "Subject" },
    { field: "print_request_status", headerName: "Status" },
  ];

  const dialogcolumns: Column[] = [
    { field: "center_name", headerName: "Center", sortable: true },
    // { field: "day", headerName: "Day" },
    { field: "year_level", headerName: "Year Level" },
    { field: "subject_name", headerName: "Subject" },
    { field: "print_request", headerName: "Status" },
  ];

  const handleDayClick = (dayEvents: any[]) => {
    setSelectedClasses(dayEvents);
    setOpenPopup(true);
  };

  console.log(selectedClasses, "selectedClasses");

  const formattedClasses = useMemo(() => {
    return (selectedClasses || []).map((classItem: any) => {
      const data = classItem?.data || {};

      return {
        id: data?.csm_id || Math.random(),

        // For API navigation
        center_id: data?.center?.center_id || data?.center_id || "-",
        csms_id: data?.csms_id || "-",
        center_name: data?.center?.center_name || data?.center_name || "-",

        yeargroup_id: data?.yeargroup?.id || data?.yeargroup_id || "-",
        year_level: data?.yeargroup?.year_group || "-",

        subject_id: data?.subject?.id || data?.subject_id || "-",
        subject_name: data?.subject?.name || "-",

        date: data?.date || "-",
        formatted_date: formatDate(data?.date),

        print_request: data?.print_request || "-",
        printing_status: data?.printing_status || "-",
        request_id: data?.req_ids || "-",
      };
    });
  }, [selectedClasses]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
          Tutor - Dashboard
        </Typography>

        <Grid container spacing={3}>
          {/* Left Column - Status Cards and Charts */}
          <Grid item xs={12} lg={5}>
            {/* Top Status Cards */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                flexWrap: "nowrap",
                gap: 2,
                mb: 3,
                overflowX: "visible",
                "@media (max-width:600px)": {
                  flexWrap: "wrap",
                  overflowX: "auto",
                  px: 1,
                },
              }}
            >
              <Grid item xs={12} md={6}>
                <StatusCard
                  title="Total Classes Assigned"
                  value={totalClassesAssigned}
                  backgroundColor="#EAF4FB"
                  color="#007BFF"
                />
              </Grid>
              {/* <Grid item xs={12} md={4}>
                <StatusCard
                  title="Last Class Taken"
                  value={lastClassSubject}
                  subtitle={
                    lastClassDate
                      ? `${lastClassYearGroup} | ${lastClassDate}`
                      : "No Classes"
                  }
                  backgroundColor="#E6F4EA"
                  color="#28A745"
                />
              </Grid> */}
              <Grid item xs={12} md={6}>
                <StatusCard
                  title="Last Booklet Request"
                  value={lastBookletSubject}
                  subtitle={
                    lastBookletRequest
                      ? `${lastBookletYearGroup} | ${lastBookletDate}`
                      : "No booklet requests yet"
                  }
                  backgroundColor="#F3EAF9"
                  color="#BC57FF"
                />
              </Grid>
            </Box>

            {/* Charts */}
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <ProgressChartCard
                  title="Request Status"
                  data={requestData}
                  chartTitle="Request Status"
                  total={requestTotal}
                  colors={["#23A346", "#FFB800", "#FF7A1A", "#E53935"]}
                />
              </Grid>
              <Grid item xs={12}>
                <ProgressChartCard
                  chartTitle="Class"
                  title="Classes Scheduled vs Completed"
                  data={classData}
                  total={classTotal}
                  colors={["#23A346", "#FFB800", "#FF7A1A", "#E53935"]}
                />
              </Grid>
              <Grid item xs={12}>
                <ProgressChartCard
                  title="Printing Status"
                  chartTitle="Printing Status"
                  data={printingData}
                  total={printingTotal}
                  colors={["#23A346", "#FFB800", "#FF7A1A", "#E53935"]}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column - Upcoming Classes Table */}
          <Grid
            item
            xs={12}
            lg={7}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              height: "100%",
            }}
          >
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                p: 2,
                boxShadow: 1,
                height: "430px", // total box height
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Table container scrolls inside */}
              <Box
                sx={{
                  borderRadius: 1,
                }}
              >
                <CalendarView
                  events={calendarEvents}
                  onDayClick={handleDayClick}
                />
              </Box>
            </Box>
            <Box
              sx={{
                bgcolor: "white",
                borderRadius: 2,
                p: 2,
                boxShadow: 1,
                height: "420px", // total box height
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight="600"
                  sx={{ color: "#017CC9", flexShrink: 0 }}
                >
                  Upcoming Scheduled Classes
                </Typography>

                <IconButton
                  onClick={(e) => {
                    setAnchorEl(e.currentTarget);
                    setTempFilters({ ...filters });
                  }}
                >
                  <FilterListIcon sx={{ fontSize: 26, color: "#017CC9" }} />
                </IconButton>
              </Box>

              <Box
                sx={{
                  flexGrow: 1,
                  overflow: "auto",
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, auto)",
                    gap: 3,
                    mb: 2,
                    alignItems: "center",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 24,
                        bgcolor: "#FF7A1A",
                        borderRadius: "4px",
                      }}
                    />
                    <Typography sx={{ fontSize: 14 }}>Not Requested</Typography>
                  </Box>

                  {/* Yellow */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 24,
                        bgcolor: "#af50bf",
                        borderRadius: "4px",
                      }}
                    />
                    <Typography sx={{ fontSize: 14 }}>Requested</Typography>
                  </Box>

                  {/* Red */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 24,
                        bgcolor: "#FFB800",
                        borderRadius: "4px",
                      }}
                    />
                    <Typography sx={{ fontSize: 14 }}>Approved</Typography>
                  </Box>

                  {/* Red */}
                  {/* Green */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 24,
                        bgcolor: "#23A346",
                        borderRadius: "4px",
                      }}
                    />
                    <Typography sx={{ fontSize: 14 }}>
                      Print Completed
                    </Typography>
                  </Box>
                  {/* Orange */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 50,
                        height: 24,
                        bgcolor: "#E53935",
                        borderRadius: "4px",
                      }}
                    />
                    <Typography sx={{ fontSize: 14 }}>
                      Rejected / Print Failed
                    </Typography>
                  </Box>
                </Box>

                <CommonTable2
                  columns={columns}
                  rows={filteredUpcomingClasses}
                  loading={ClassesLoading}
                  statusOpen={true}
                  onRowClick={(row) => {
                    const selectedData = {
                      id: row.csms_id,
                      center_id: row.center_id,
                      center_name: row.center,
                      yeargroup_id: row.YearLevel_id,
                      yeargroup_name: row.YearLevel,
                      subject_id: row.subject_id,
                      subject_name: row.subject,
                      date: row.date,
                    };
                    if (row.print_request_status === "not_yet_requested") {
                      navigate("/study_materials", {
                        state: selectedData,
                      });
                    } else {
                      navigate("/my_requests", {
                        state: {
                          request_id: row.request_ids,
                        },
                      });
                    }
                  }}
                />
              </Box>
            </Box>
          </Grid>

          {/* Enhanced Filter Menu with Dropdowns for All Filters */}
          <Menu
            anchorEl={anchorEl}
            open={dropdownOpen}
            onClose={() => setAnchorEl(null)}
            PaperProps={{
              sx: {
                width: 500,
                maxHeight: 600,
                p: 3,
                borderRadius: 3,
                boxShadow: 3,
                overflow: "visible",
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <Paper elevation={0} sx={{ borderRadius: 2, mb: 2 }}>
              <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                Advanced Filters
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mb: 2 }}
              >
                Select options below to filter upcoming classes. Active filters
                will be highlighted.
              </Typography>

              {/* Active Filters Chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {Object.entries(filters).map(([key, value]: [string, any]) => {
                  if (!value || !Array.isArray(value) || value.length === 0)
                    return null;
                  return value.map((item: string) => {
                    const options = getOptions(key);
                    const label =
                      options.find((opt: any) => opt.value === item)?.label ||
                      item;
                    return (
                      <Chip
                        key={`${key}-${item}`}
                        label={`${columnFilters[key]?.label}: ${label}`}
                        onDelete={() => {
                          const newValue = value.filter(
                            (v: string) => v !== item
                          );
                          const newFilters = { ...filters };
                          if (newValue.length === 0) {
                            delete newFilters[key];
                          } else {
                            newFilters[key] = newValue;
                          }
                          setFilters(newFilters);
                          setTempFilters(newFilters);
                        }}
                        variant="outlined"
                        color="primary"
                        size="small"
                      />
                    );
                  });
                })}
              </Box>

              <Divider sx={{ mb: 2 }} />
            </Paper>

            <Grid container spacing={2}>
              {columns.map((col) => {
                const filterConfig = columnFilters[col.field];
                if (!filterConfig) return null;

                const options =
                  typeof filterConfig.options === "function"
                    ? filterConfig.options()
                    : filterConfig.options;

                return (
                  <Grid item xs={12} sm={6} key={col.field}>
                    <Typography
                      fontSize={13}
                      fontWeight={600}
                      sx={{ mb: 0.5, color: "#19191aff" }}
                    >
                      {filterConfig.label}
                    </Typography>
                    {filterConfig.type === "select" ? (
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <Select
                          multiple
                          value={tempFilters[col.field] || []}
                          onChange={(e) => {
                            let newValue = e.target.value as string[];
                            const allValue = "";
                            if (newValue.includes(allValue)) {
                              newValue = [];
                            }
                            setTempFilters((prev: any) => ({
                              ...prev,
                              [col.field]: newValue,
                            }));
                          }}
                          displayEmpty
                          renderValue={(selected) => {
                            if ((selected as string[]).length === 0)
                              return "All";
                            return (selected as string[])
                              .map(
                                (v) =>
                                  options.find((opt: any) => opt.value === v)
                                    ?.label || v
                              )
                              .join(", ");
                          }}
                          sx={{
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#e1e3e4ff",
                            },
                            "&:hover .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#009ddf",
                            },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#009ddf",
                              borderWidth: 2,
                            },
                          }}
                        >
                          {options.map((opt: any) => (
                            <MuiMenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MuiMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : null}
                  </Grid>
                );
              })}
            </Grid>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 2,
                mt: 3,
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={() => {
                  const cleared = {};
                  setFilters(cleared);
                  setTempFilters(cleared);
                  setAnchorEl(null);
                }}
                sx={{ minWidth: 80, fontSize: "12px", textTransform: "none" }}
              >
                Clear All
              </Button>
              <Button
                variant="contained"
                sx={{
                  bgcolor: "#009ddf",
                  color: "white",
                  fontSize: "12px",
                  textTransform: "none",
                  minWidth: 80,
                  "&:hover": { bgcolor: "#009ddf" },
                }}
                size="small"
                onClick={() => {
                  const cleaned = { ...tempFilters };
                  Object.keys(cleaned).forEach((k) => {
                    if (Array.isArray(cleaned[k]) && cleaned[k].length === 0) {
                      delete cleaned[k];
                    }
                  });
                  setFilters(cleaned);
                  setAnchorEl(null);
                }}
              >
                Apply Filters
              </Button>
            </Box>
          </Menu>

          <Dialog
            open={openPopup}
            onClose={() => setOpenPopup(false)}
            fullWidth
            maxWidth="md"
          >
            <DialogTitle
              sx={{ fontWeight: "600", position: "relative", pr: 6 }}
            >
              Scheduled Classes
              {/* Close Icon */}
              <IconButton
                aria-label="close"
                onClick={() => setOpenPopup(false)}
                sx={{
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <DialogContent>
              {/* Legend */}
              <Box sx={{ display: "flex", gap: 3, mb: 2, flexWrap: "wrap" }}>
                {[
                  { label: "Not Requested", color: "#FF7A1A" },
                  { label: "Requested", color: "#af50bf" },
                  { label: "Approved", color: "#FFB800" },
                  { label: "Print Completed", color: "#23A346" },
                  { label: "Rejected / Print Failed", color: "#E53935" },
                ].map((item, i) => (
                  <Box
                    key={i}
                    sx={{ display: "flex", alignItems: "center", gap: 1 }}
                  >
                    <Box
                      sx={{
                        width: 30,
                        height: 18,
                        bgcolor: item.color,
                        borderRadius: "4px",
                      }}
                    />
                    <Typography sx={{ fontSize: 14 }}>{item.label}</Typography>
                  </Box>
                ))}
              </Box>

              <CommonTable2
                columns={dialogcolumns}
                rows={formattedClasses}
                loading={dateLoading}
                statusOpen={true}
                onRowClick={(row) => {
                  const selectedData = {
                    id: row.csms_id,
                    center_id: row.center_id,
                    center_name: row.center_name,
                    yeargroup_id: row.yeargroup_id,
                    yeargroup_name: row.year_level,
                    subject_id: row.subject_id,
                    subject_name: row.subject_name,
                    print_request: row.print_request,
                    date: row.date,
                  };

                  if (row.print_request === "not_yet_requested") {
                    navigate("/study_materials", {
                      state: selectedData,
                    });
                  } else {
                    navigate("/my_requests", {
                      state: {
                        request_id: row.request_id,
                      },
                    });
                  }
                }}
              />
            </DialogContent>
          </Dialog>
        </Grid>
        <AlertBox
          open={open}
          message="⏳ Print request pending — upcoming class is scheduled within 24 hours."
          onClose={() => {
            sessionStorage.setItem("printerAlertClosed", "true");
            setOpen(false);
          }}
          variant="info"
        />
      </Box>
    </LocalizationProvider>
  );
};

export default TutorDashboard;
